import { type ReactNode, useEffect } from 'react'

import { refreshSession } from '../../shared/api'
import { currentTranslation } from '../../shared/i18n'

import { useAuthStore } from './hooks'

/** Restores the session from the refresh cookie on load; schedules silent refreshes. */
export function AuthBootstrap({ children }: { children: ReactNode }) {
  const status = useAuthStore((s) => s.status)
  const token = useAuthStore((s) => s.accessToken)

  useEffect(() => {
    if (status !== 'unknown') return
    void refreshSession().then((session) => {
      if (!session) useAuthStore.getState().clear()
    })
  }, [status])

  useEffect(() => {
    if (!token) return
    // Refresh one minute before the short-lived access token expires.
    const payload = JSON.parse(
      atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')),
    ) as {
      exp: number
    }

    const delay = Math.max(5_000, payload.exp * 1000 - Date.now() - 60_000)
    const timer = setTimeout(() => void refreshSession(), delay)

    return () => clearTimeout(timer)
  }, [token])

  if (status === 'unknown') {
    return (
      <div className="center-fill" style={{ minHeight: '100vh' }}>
        <span
          className="spinner"
          role="status"
          aria-label={currentTranslation().t.common.loading}
        />
      </div>
    )
  }

  return <>{children}</>
}
